import {
  Accordion,
  AccordionDetails,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { FormikErrors, FormikTouched } from 'formik';
import { memo, useCallback } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { SubjectDataVM } from 'shell/view-models/search-anpr';
import ChooseDate from 'shell/components/shared/choice-date';
import InputKeyboard from 'shell/components/shared/input-keyboard';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { noop } from 'shell/utils/noop';
import ListGender from 'shell/components/shared/gender';
import ItalianForeignState from './italian-foreign-state';
import { FiltersPersonSearch } from 'shell/constants/filters-person-search';

interface FiltersSubjectDataProps {
  values?: SubjectDataVM;
  open?: boolean;
  onOpenAccordion: (value: FiltersPersonSearch) => void;
  onChange: (key: keyof SubjectDataVM, value: SubjectDataVM[keyof SubjectDataVM]) => void;
  onChangeValues: (object: SubjectDataVM) => void;
  errors?: FormikErrors<SubjectDataVM>;
  touched?: FormikTouched<SubjectDataVM>;
}

const FiltersSubjectDataComponent = ({
  values,
  open,
  onOpenAccordion,
  onChange,
  onChangeValues,
  errors,
  touched,
}: FiltersSubjectDataProps): React.ReactElement => {
  const handleSurname = useCallback(
    (value) => {
      onChange('surname', value);
    },
    [onChange]
  );

  const handleWithoutSurname = useCallback(() => {
    onChange('withoutSurname', !values?.withoutSurname);
  }, [onChange, values?.withoutSurname]);

  const handleWithoutName = useCallback(() => {
    onChange('withoutName', !values?.withoutName);
  }, [onChange, values?.withoutName]);

  const handleName = useCallback(
    (value) => {
      onChange('name', value);
    },
    [onChange]
  );

  const handleBirthDate = useCallback(
    (value, choice) => {
      new Promise((resolve) => resolve(onChange('birthday', value))).then(() => {
        onChange('birthdayChoice', choice);
      });
    },
    [onChange]
  );

  const handleReferenceDate = useCallback(
    (value) => {
      onChange('referenceDate', value);
    },
    [onChange]
  );

  const handleGender = useCallback(
    (event) => {
      onChange('gender', event.target.value);
    },
    [onChange]
  );

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        onOpenAccordion(FiltersPersonSearch.SubjectData);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-filters-subject-data-content"
        id="panel-person-filters-subject-data-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>Ricerca per dati del soggetto</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item container spacing={2}>
            <Grid item container spacing={2} xs={12} md={9} alignItems="center">
              <Grid item xs={12} md={9}>
                <InputKeyboard
                  touched={touched?.surname && Boolean(errors?.surname)}
                  error={touched?.surname ? errors?.surname : ''}
                  id="surname"
                  label={`Cognome ${
                    values?.withoutSurname ||
                    (values?.municipalityId && values?.istatCode) ||
                    values?.anprId
                      ? ''
                      : '*'
                  }`}
                  value={values?.surname}
                  onChange={handleSurname}
                  inputProps={{
                    maxLength: '80',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="withoutSurname"
                      name="withoutSurname"
                      checked={values?.withoutSurname}
                      onChange={handleWithoutSurname}
                      inputProps={{ 'aria-label': 'Persona senza cognome' }}
                    />
                  }
                  label="Persona senza cognome"
                />
              </Grid>
            </Grid>
            <Grid item container spacing={2} xs={12} md={9} alignItems="center">
              <Grid item xs={12} md={9}>
                <InputKeyboard
                  touched={touched?.name && Boolean(errors?.name)}
                  error={touched?.name ? errors?.name : ''}
                  id="name"
                  label={`Nome ${
                    values?.withoutName ||
                    (values?.municipalityId && values?.istatCode) ||
                    values?.anprId
                      ? ''
                      : '*'
                  }`}
                  value={values?.name}
                  onChange={handleName}
                  inputProps={{
                    maxLength: '80',
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id="withoutName"
                      name="withoutName"
                      checked={values?.withoutName}
                      onChange={handleWithoutName}
                      inputProps={{ 'aria-label': 'Persona senza nome' }}
                    />
                  }
                  label="Persona senza nome"
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={3}>
              <ListGender value={values?.gender || ''} onChange={handleGender} />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12} container spacing={2} alignItems="center">
              <ChooseDate
                selected={values?.birthday}
                choice={values?.birthdayChoice}
                onChange={handleBirthDate}
                helperText={errors?.birthday}
                error={touched?.birthday && Boolean(errors?.birthday)}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <ItalianForeignState
            isForeign={values?.isForeign}
            foreignStateBirth={values?.foreignStateBirth}
            foreignMunicipality={values?.foreignMunicipality}
            provinceOfBirth={values?.provinceOfBirth}
            municipalityOfBirth={values?.municipalityOfBirth}
            locality={values?.locality}
            exceptionalBirthplace={values?.exceptionalBirthplace}
            values={values}
            onChange={onChange}
            onChangeValues={onChangeValues || noop}
          />
          <Grid container item spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Identificativo del Comune"
                label="Identificativo del Comune"
                id="municipalityId"
                name="municipalityId"
                fullWidth
                type="text"
                variant="outlined"
                inputProps={{
                  maxLength: '20',
                }}
                value={values?.municipalityId}
                onChange={(event) => onChange('municipalityId', event.target.value)}
                error={touched?.municipalityId && Boolean(errors?.municipalityId)}
                helperText={errors?.municipalityId}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Codice Istat"
                label="Codice Istat"
                id="istatCode"
                name="istatCode"
                fullWidth
                type="text"
                inputProps={{
                  maxLength: '6',
                }}
                variant="outlined"
                value={values?.istatCode}
                onChange={(event) => onChange('istatCode', event.target.value)}
                error={touched?.istatCode && Boolean(errors?.istatCode)}
                helperText={errors?.istatCode}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <PmiTextField
                aria-label="Identificativo ANPR"
                label="Identificativo ANPR"
                id="anprId"
                name="anprId"
                fullWidth
                type="text"
                inputProps={{
                  maxLength: '15',
                }}
                variant="outlined"
                value={values?.anprId}
                onChange={(event) => onChange('anprId', event.target.value)}
                error={touched?.anprId && Boolean(errors?.anprId)}
                helperText={errors?.anprId}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
          <Grid container item spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Data di riferimento scheda"
                value={values?.referenceDate || null}
                onChange={handleReferenceDate}
                renderInput={(params) => (
                  <PmiTextField
                    {...params}
                    id="referenceDate"
                    fullWidth
                    helperText={errors?.referenceDate}
                    error={touched?.referenceDate && Boolean(errors?.referenceDate)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

FiltersSubjectDataComponent.displayName = 'Filters Person Subject Data component';

const FiltersSubjectData = memo(FiltersSubjectDataComponent);

export default FiltersSubjectData;
